<template>
  <div class="modern-ai-card">
    <!-- Header Profissional -->
    <div class="card-header">
      <div class="header-left">
        <div class="icon-wrapper">
          <svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" 
                  d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
          </svg>
        </div>
        <div class="header-info">
          <h3 class="title">{{ t('integracao.aiTitle') }}</h3>
          <p class="subtitle">{{ t('integracao.aiSubtitle') }}</p>
        </div>
      </div>
      <div class="header-right">
        <div v-if="connection && connection.status === 'CONFIGURED'" class="badge badge-success">
          <span class="badge-dot"></span>
          <span>{{ t('integracao.configured') }}</span>
        </div>
        <div v-else class="badge badge-warning">
          <span class="badge-dot"></span>
          <span>{{ t('integracao.notConfigured') }}</span>
        </div>
      </div>
    </div>

    <!-- Conteúdo -->
    <div class="card-body">
      <!-- Grid de Configuração -->
      <div class="config-grid">
        <div class="field-group">
          <label class="field-label">
            <svg class="label-icon" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M6 2a2 2 0 00-2 2v12a2 2 0 002 2h8a2 2 0 002-2V7.414A2 2 0 0015.414 6L12 2.586A2 2 0 0010.586 2H6zm5 6a1 1 0 10-2 0v3.586l-1.293-1.293a1 1 0 10-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 11.586V8z" clip-rule="evenodd" />
            </svg>
            {{ t('integracao.provider') }}
          </label>
          <select v-model="form.provider" class="field-input">
            <option value="openai">OpenAI</option>
            <option value="azure-openai">Azure OpenAI</option>
            <option value="ollama">Ollama (self-hosted)</option>
            <option value="custom">{{ t('integracao.providerCustom') }}</option>
          </select>
        </div>

        <div class="field-group">
          <label class="field-label">
            <svg class="label-icon" viewBox="0 0 20 20" fill="currentColor">
              <path d="M13 7H7v6h6V7z" />
              <path fill-rule="evenodd" d="M7 2a1 1 0 012 0v1h2V2a1 1 0 112 0v1h2a2 2 0 012 2v2h1a1 1 0 110 2h-1v2h1a1 1 0 110 2h-1v2a2 2 0 01-2 2h-2v1a1 1 0 11-2 0v-1H9v1a1 1 0 11-2 0v-1H5a2 2 0 01-2-2v-2H2a1 1 0 110-2h1V9H2a1 1 0 010-2h1V5a2 2 0 012-2h2V2zM5 5h10v10H5V5z" clip-rule="evenodd" />
            </svg>
            {{ t('integracao.model') }}
          </label>
          <select v-model="form.model" class="field-input">
            <option v-for="opt in modelOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
          </select>
          <input v-if="form.model === '__custom__'" v-model="customModel" 
                 :placeholder="t('integracao.customModelPlaceholder')" 
                 class="field-input mt-2" />
        </div>

        <div class="field-group field-full">
          <label class="field-label">
            <svg class="label-icon" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M12.586 4.586a2 2 0 112.828 2.828l-3 3a2 2 0 01-2.828 0 1 1 0 00-1.414 1.414 4 4 0 005.656 0l3-3a4 4 0 00-5.656-5.656l-1.5 1.5a1 1 0 101.414 1.414l1.5-1.5zm-5 5a2 2 0 012.828 0 1 1 0 101.414-1.414 4 4 0 00-5.656 0l-3 3a4 4 0 105.656 5.656l1.5-1.5a1 1 0 10-1.414-1.414l-1.5 1.5a2 2 0 11-2.828-2.828l3-3z" clip-rule="evenodd" />
            </svg>
            {{ t('integracao.baseUrl') }}
            <span class="optional-badge">{{ t('common.optional') }}</span>
          </label>
          <input v-model="form.baseUrl" 
                 placeholder="https://api.openai.com/v1" 
                 class="field-input" />
        </div>

        <div class="field-group field-full">
          <label class="field-label">
            <svg class="label-icon" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M18 8a6 6 0 01-7.743 5.743L10 14l-1 1-1 1H6v2H2v-4l4.257-4.257A6 6 0 1118 8zm-6-4a1 1 0 100 2 2 2 0 012 2 1 1 0 102 0 4 4 0 00-4-4z" clip-rule="evenodd" />
            </svg>
            {{ t('integracao.apiKey') }}
          </label>
          <div class="input-group">
            <input :type="showKey ? 'text' : 'password'" 
                   v-model="form.apiKey" 
                   placeholder="sk-proj-..." 
                   autocomplete="off"
                   class="field-input-group" />
            <button @click="showKey = !showKey" 
                    type="button"
                    class="toggle-btn">
              <svg v-if="showKey" class="w-5 h-5" viewBox="0 0 20 20" fill="currentColor">
                <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                <path fill-rule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clip-rule="evenodd" />
              </svg>
              <svg v-else class="w-5 h-5" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M3.707 2.293a1 1 0 00-1.414 1.414l14 14a1 1 0 001.414-1.414l-1.473-1.473A10.014 10.014 0 0019.542 10C18.268 5.943 14.478 3 10 3a9.958 9.958 0 00-4.512 1.074l-1.78-1.781zm4.261 4.26l1.514 1.515a2.003 2.003 0 012.45 2.45l1.514 1.514a4 4 0 00-5.478-5.478z" clip-rule="evenodd" />
                <path d="M12.454 16.697L9.75 13.992a4 4 0 01-3.742-3.741L2.335 6.578A9.98 9.98 0 00.458 10c1.274 4.057 5.065 7 9.542 7 .847 0 1.669-.105 2.454-.303z" />
              </svg>
            </button>
          </div>
          <p class="field-hint">{{ t('integracao.apiKeyHint') }}</p>
        </div>
      </div>

      <!-- Ações -->
      <div class="action-bar">
        <button @click="salvar" :disabled="loading" class="btn btn-primary">
          <svg v-if="loading" class="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <svg v-else class="w-5 h-5" viewBox="0 0 20 20" fill="currentColor">
            <path d="M7.707 10.293a1 1 0 10-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 11.586V6h5a2 2 0 012 2v7a2 2 0 01-2 2H4a2 2 0 01-2-2V8a2 2 0 012-2h5v5.586l-1.293-1.293zM9 4a1 1 0 012 0v2H9V4z" />
          </svg>
          <span>{{ loading ? t('integracao.saving') : t('integracao.saveConfig') }}</span>
        </button>

        <button @click="testarConexao" :disabled="testing || !connection" class="btn btn-secondary">
          <svg class="w-5 h-5" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
          </svg>
          <span>{{ testing ? t('integracao.testing') : t('integracao.testConnection') }}</span>
        </button>

        <button @click="openContext = true" class="btn btn-outline">
          <svg class="w-5 h-5" viewBox="0 0 20 20" fill="currentColor">
            <path d="M7 3a1 1 0 000 2h6a1 1 0 100-2H7zM4 7a1 1 0 011-1h10a1 1 0 110 2H5a1 1 0 01-1-1zM2 11a2 2 0 012-2h12a2 2 0 012 2v4a2 2 0 01-2 2H4a2 2 0 01-2-2v-4z" />
          </svg>
          <span>{{ t('integracao.manageContext') }}</span>
        </button>
      </div>
    </div>

    <AIContextModal :workspaceId="workspaceId" :open="openContext" @close="openContext = false" />
  </div>
</template>

<script>
import { ref, onMounted, computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import api from '@/services/api'
import Swal from 'sweetalert2'
import AIContextModal from './AIContextModal.vue'

export default {
  name: 'AIIntegrationCard',
  components: { AIContextModal },
  props: {
    workspaceId: { type: Number, required: true }
  },
  emits: ['connection-updated'],
  setup(props, { emit }) {
    const { t } = useI18n()
    const connection = ref(null)
    const loading = ref(false)
    const testing = ref(false)
    const showKey = ref(false)
    const openContext = ref(false)
    const form = ref({ provider: 'openai', model: '', baseUrl: '', apiKey: '' })
    const customModel = ref('')

    const modelOptions = computed(() => {
      const commonCustom = [{ value: '__custom__', label: t('integracao.customModel') }]
      // Lista alinhada aos modelos públicos da OpenAI (texto/chat principais)
      const openAIModels = [
        { value: 'gpt-5', label: 'gpt-5' },
        { value: 'gpt-5-mini', label: 'gpt-5-mini' },
        { value: 'gpt-5-nano', label: 'gpt-5-nano' },
        { value: 'gpt-5-pro', label: 'gpt-5-pro' },
        { value: 'gpt-4.1', label: 'gpt-4.1' },
        { value: 'gpt-4.1-mini', label: 'gpt-4.1-mini' },
        { value: 'gpt-4.1-nano', label: 'gpt-4.1-nano' },
        { value: 'gpt-4o', label: 'gpt-4o' },
        { value: 'gpt-4o-mini', label: 'gpt-4o-mini' },
      ]
      switch (form.value.provider) {
        case 'openai':
        case 'azure-openai':
          return [...openAIModels, ...commonCustom]
        case 'ollama':
          return [
            { value: 'llama3.1:latest', label: 'llama3.1:latest' },
            { value: 'mistral:latest', label: 'mistral:latest' },
            { value: 'qwen2.5-coder:latest', label: 'qwen2.5-coder:latest' },
            ...commonCustom
          ]
        default:
          return [...openAIModels, ...commonCustom]
      }
    })

    const carregar = async () => {
      try {
        const { data } = await api.get(`/ai/workspace/${props.workspaceId}`)
        connection.value = data
        // Preenche form com retorno (sem revelar apiKey completa)
        form.value.provider = data?.provider || 'openai'
        // Ajusta modelo: se não pertencer às opções atuais, define como personalizado
        const currentOptions = modelOptions.value.map(o => o.value)
        if (data?.model && !currentOptions.includes(data.model)) {
          form.value.model = '__custom__'
          customModel.value = data.model
        } else {
          form.value.model = data?.model || ''
        }
        form.value.baseUrl = data?.baseUrl || ''
        form.value.apiKey = ''
      } catch (e) {
        connection.value = null
      }
    }

    // Quando o provedor muda, se o modelo atual não existir na nova lista e não for custom, seleciona a primeira opção
    watch(() => form.value.provider, () => {
      const opts = modelOptions.value.map(o => o.value)
      if (!opts.includes(form.value.model)) {
        form.value.model = opts[0] || '__custom__'
      }
    })

    const salvar = async () => {
      try {
        loading.value = true
        const modelToPersist = form.value.model === '__custom__' ? (customModel.value || '') : form.value.model
        if (!modelToPersist) {
          await Swal.fire({
            icon: 'warning',
            title: t('integracao.swal.attention'),
            text: t('integracao.swal.modelRequired'),
            confirmButtonColor: '#3B82F6'
          })
          loading.value = false
          return
        }
        const payload = {
          provider: form.value.provider,
          model: modelToPersist,
          baseUrl: form.value.baseUrl || null
        }
        // Só inclui apiKey no payload se o usuário digitou algo novo (não vazio e não mascarado)
        if (form.value.apiKey && form.value.apiKey.trim() && !form.value.apiKey.includes('*')) {
          payload.apiKey = form.value.apiKey.trim()
        }
        const { data } = await api.put(`/ai/workspace/${props.workspaceId}`, payload)
        connection.value = data
        emit('connection-updated', data)
        // Limpa o campo apiKey para não deixar visível
        form.value.apiKey = ''
        await Swal.fire({
          icon: 'success',
          title: t('integracao.swal.success'),
          text: t('integracao.swal.aiSaved'),
          timer: 2000,
          showConfirmButton: false
        })
      } catch (e) {
        console.error('Erro ao salvar integração IA:', e)
        await Swal.fire({
          icon: 'error',
          title: t('integracao.swal.error'),
          text: e?.response?.data?.error || t('integracao.swal.aiSaveError'),
          confirmButtonColor: '#EF4444'
        })
      } finally {
        loading.value = false
      }
    }

    const testarConexao = async () => {
      if (!connection.value) {
        await Swal.fire({
          icon: 'warning',
          title: t('integracao.swal.attention'),
          text: t('integracao.swal.configureFirst'),
          confirmButtonColor: '#3B82F6'
        })
        return
      }
      try {
        testing.value = true
        const { data } = await api.post(`/ai/workspace/${props.workspaceId}/ping`, { prompt: 'ping' })
        await Swal.fire({
          icon: 'success',
          title: t('integracao.swal.success'),
          text: data?.message || t('integracao.swal.testSuccess'),
          timer: 2000,
          showConfirmButton: false
        })
      } catch (e) {
        console.error('Erro no teste de IA:', e)
        await Swal.fire({
          icon: 'error',
          title: t('integracao.swal.connectionError'),
          text: e?.response?.data?.error || t('integracao.swal.testError'),
          confirmButtonColor: '#EF4444'
        })
      } finally {
        testing.value = false
      }
    }

    onMounted(() => { carregar() })

    return { t, connection, loading, testing, showKey, openContext, form, modelOptions, customModel, salvar, testarConexao }
  }
}
</script>

<style scoped>
/* Card Container */
.modern-ai-card {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04), 0 1px 3px rgba(0, 0, 0, 0.06);
  overflow: hidden;
  transition: box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}
.modern-ai-card:hover {
  box-shadow: 0 8px 20px rgba(109, 40, 217, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08);
}

/* Dark mode usando :deep */
:deep(.dark) .modern-ai-card,
.dark .modern-ai-card {
  background: #1f2937 !important;
  border-color: #374151 !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3), 0 1px 3px rgba(0, 0, 0, 0.5) !important;
}
:deep(.dark) .modern-ai-card:hover,
.dark .modern-ai-card:hover {
  box-shadow: 0 8px 20px rgba(139, 92, 246, 0.2), 0 2px 8px rgba(0, 0, 0, 0.4) !important;
  border-color: #4b5563 !important;
}

/* Header */
.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 28px 32px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  background: linear-gradient(135deg, #6d28d9 0%, #8b5cf6 100%);
  position: relative;
  overflow: hidden;
}
.card-header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: radial-gradient(circle at top right, rgba(255, 255, 255, 0.1) 0%, transparent 60%);
  pointer-events: none;
}
:deep(.dark) .card-header,
.dark .card-header {
  background: linear-gradient(135deg, #581c87 0%, #7c3aed 100%) !important;
  border-color: rgba(255, 255, 255, 0.05) !important;
}

.header-left {
  display: flex;
  gap: 16px;
  align-items: center;
  position: relative;
  z-index: 1;
}

.icon-wrapper {
  width: 64px;
  height: 64px;
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(12px);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid rgba(255, 255, 255, 0.3);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
}
.icon-wrapper:hover {
  transform: scale(1.05);
  border-color: rgba(255, 255, 255, 0.5);
}

.icon {
  width: 32px;
  height: 32px;
  color: white;
  stroke-width: 2;
}

.header-info .title {
  font-size: 22px;
  font-weight: 700;
  color: white;
  margin: 0 0 4px 0;
  letter-spacing: -0.025em;
}

.header-info .subtitle {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.8);
  margin: 0;
  font-weight: 400;
}

/* Badges */
.badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 18px;
  border-radius: 12px;
  font-size: 13px;
  font-weight: 700;
  backdrop-filter: blur(12px);
  position: relative;
  z-index: 1;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.badge-success {
  background: rgba(16, 185, 129, 0.2);
  color: #ffffff;
  border: 1px solid rgba(255, 255, 255, 0.4);
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
}

.badge-warning {
  background: rgba(251, 191, 36, 0.2);
  color: #ffffff;
  border: 1px solid rgba(255, 255, 255, 0.4);
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
}

.badge-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: currentColor;
  animation: pulse-dot 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

@keyframes pulse-dot {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

/* Body */
.card-body {
  padding: 32px;
  background: #ffffff;
}
:deep(.dark) .card-body,
.dark .card-body {
  background: #1f2937 !important;
}

/* Config Grid */
.config-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  margin-bottom: 24px;
}

.field-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.field-group.field-full {
  grid-column: 1 / -1;
}

.field-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 4px;
}
.dark .field-label {
  color: #d1d5db;
}

.label-icon {
  width: 16px;
  height: 16px;
  color: #6d28d9;
}
.dark .label-icon {
  color: #a78bfa;
}

.optional-badge {
  margin-left: auto;
  padding: 2px 8px;
  background: #e5e7eb;
  color: #6b7280;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 500;
}
.dark .optional-badge {
  background: #374151;
  color: #9ca3af;
}

/* Inputs */
.field-input {
  width: 100%;
  padding: 12px 14px;
  background: #ffffff;
  border: 2px solid #e5e7eb;
  border-radius: 10px;
  font-size: 14px;
  color: #1f2937;
  transition: all 0.2s;
  outline: none;
}
.field-input:focus {
  border-color: #6d28d9;
  box-shadow: 0 0 0 3px rgba(109, 40, 217, 0.1);
}
.dark .field-input {
  background: #374151;
  border-color: #4b5563;
  color: #f3f4f6;
}
.dark .field-input:focus {
  border-color: #8b5cf6;
  box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.1);
}

/* Select específico */
select.field-input {
  appearance: none !important;
  -webkit-appearance: none !important;
  -moz-appearance: none !important;
  background-color: #ffffff;
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e") !important;
  background-position: right 10px center !important;
  background-repeat: no-repeat !important;
  background-size: 20px !important;
  padding-right: 40px !important;
}
.dark select.field-input {
  background-color: #374151 !important;
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%239ca3af' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e") !important;
}

/* Options do select */
select.field-input option {
  background: #ffffff;
  color: #1f2937;
  padding: 8px;
}
.dark select.field-input option {
  background: #374151;
  color: #f3f4f6;
}

/* Input Group */
.input-group {
  display: flex;
  gap: 0;
  background: #ffffff;
  border: 2px solid #e5e7eb;
  border-radius: 10px;
  overflow: hidden;
  transition: all 0.2s;
}
.input-group:focus-within {
  border-color: #6d28d9;
  box-shadow: 0 0 0 3px rgba(109, 40, 217, 0.1);
}
.dark .input-group {
  background: #374151;
  border-color: #4b5563;
}
.dark .input-group:focus-within {
  border-color: #8b5cf6;
  box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.1);
}

.field-input-group {
  flex: 1;
  padding: 12px 14px;
  background: transparent;
  border: none;
  font-size: 14px;
  color: #1f2937;
  outline: none;
}
.dark .field-input-group {
  color: #f3f4f6;
}

.toggle-btn {
  padding: 0 14px;
  background: #f3f4f6;
  border: none;
  border-left: 1px solid #e5e7eb;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
}
.toggle-btn:hover {
  background: #e5e7eb;
  color: #374151;
}
.dark .toggle-btn {
  background: #4b5563;
  border-color: #6b7280;
  color: #9ca3af;
}
.dark .toggle-btn:hover {
  background: #6b7280;
  color: #f3f4f6;
}

.field-hint {
  font-size: 12px;
  color: #6b7280;
  margin: 4px 0 0 0;
  line-height: 1.5;
}
.dark .field-hint {
  color: #9ca3af;
}

/* Action Bar */
.action-bar {
  display: flex;
  gap: 12px;
  padding-top: 20px;
  border-top: 1px solid #e5e7eb;
}
.dark .action-bar {
  border-color: #374151;
}

.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 20px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  outline: none;
  border: none;
  white-space: nowrap;
}

.btn-primary {
  background: linear-gradient(135deg, #6d28d9 0%, #8b5cf6 100%);
  color: white;
  box-shadow: 0 2px 8px rgba(109, 40, 217, 0.3);
}
.btn-primary:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(109, 40, 217, 0.4);
}
.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-secondary {
  background: #f3f4f6;
  color: #374151;
  border: 2px solid #e5e7eb;
}
.btn-secondary:hover:not(:disabled) {
  background: #e5e7eb;
  border-color: #d1d5db;
}
.dark .btn-secondary {
  background: #374151;
  color: #f3f4f6;
  border-color: #4b5563;
}
.dark .btn-secondary:hover:not(:disabled) {
  background: #4b5563;
  border-color: #6b7280;
}
.btn-secondary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-outline {
  background: transparent;
  color: #6d28d9;
  border: 2px solid #6d28d9;
}
.btn-outline:hover {
  background: #6d28d9;
  color: white;
}
.dark .btn-outline {
  color: #a78bfa;
  border-color: #a78bfa;
}
.dark .btn-outline:hover {
  background: #a78bfa;
  color: #1f2937;
}

/* Animations */
@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.w-5 { width: 20px; }
.h-5 { height: 20px; }
.mt-2 { margin-top: 8px; }
</style>


